import { useCallback, useEffect, useMemo } from "react";
import { MST2010_R01Filter, useMST2010_R01Store } from "src/containers/mes/MST/MST2010_R01/Store_MST2010_R01";

import { downloadSettingExcel } from "src/utils/blob/blobUtil";

import { CustomerResponseDto } from "src/dtos/customer/customerResponseDto";

import { AgGridReact } from "ag-grid-react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	MST2010_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';
import { createMST2010_R01, deleteMST2010_R01, downloadExcel_MST2010_R01, getMST2010_R01AllCount, getMST2010_R01List, getMST2010_R01One, patchMST2010_R01 } from "./Api_MST2010_R01";
import { defaultMST2010_R01Dto, MST2010_R01Dto } from "./Dto_MST2010_R01";


interface Props {

	gridRef: React.RefObject<AgGridReact<MST2010_R01Dto> | null>;
	setMessage: (message: string) => void;
	
}

export const Content_MST2010_R01 = (props: Props) => {
	const {
		gridRef,
		setMessage,
	} = props;

	useFetchActionbarAuth("MST2010_R01");

	
	const { 
		selectedMST2010_R01, setSelectedMST2010_R01,
		MST2010_R01List, setMST2010_R01List, 
		editMST2010_R01, setEditMST2010_R01, 
		initSelectedMST2010_R01,
		initData,
	} = useMST2010_R01Store();

	

	const { areaCdList, moneyCdList, customTypeList, fetchCustomCdCode } = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	const { 
		MST2010_R01Filter, setMST2010_R01Filter,
		setMST2010_R01PastFilter,
		setAllCount,
		setScrollTop,
		} = useMST2010_R01Store();

		const handleExcelClick = useCallback(async () => {
	
			setLoadingSpinner(true);
	
			const response = await downloadExcel_MST2010_R01(
				'',
				getParams(MST2010_R01Filter),
				MST2010_R01List.length
			);
	
			downloadSettingExcel({ responseData: response.data.data, fileName: '거래처 정보등록-excel.xlsx' });
	
			setLoadingSpinner(false);
		
		}, [MST2010_R01Filter, MST2010_R01List, setLoadingSpinner]);

	const getParams = (filter: Partial<MST2010_R01Filter>) => {
		return {
			areaCd:     filter.areaCd,
			customCd:   filter.customCd,
			customType: filter.customType,
		};
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST2010_R01AllCount(getParams(MST2010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST2010_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);

		const responseData = await getMST2010_R01List(
			'',
			getParams(MST2010_R01Filter),
			MST2010_R01List.length
		);

		let newList = [...responseData.data.data ]
		setMST2010_R01List(newList);

		const processAllCount = await renewalAllCount();

		if (newList && newList.length > 0) {

			gridRefSelectedRowHandler(
				gridRef,
				setSelectedMST2010_R01,
				() => true,// () => !newList.find(value => value.customCd === editMST2010_R01['customCd'])
			);
			setMST2010_R01PastFilter(MST2010_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning('조회된 데이터가 없습니다.');
			handleCreateClick();
			setSelectedMST2010_R01(undefined);
		}
		setLoadingSpinner(false);
		return newList;
	}, [MST2010_R01Filter, MST2010_R01List, gridRef, editMST2010_R01, setMST2010_R01List, setSelectedMST2010_R01, setScrollTop])

	const defaultMST2010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST2010_R01_ROW_VALIDATE, {
				areaCdList,
				customTypeList,
				moneyCdList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultMST2010_R01Dto };
		}

		return {
			...defaultMST2010_R01Dto,
			areaCd: areaCdList[0].minorCd,
			moneyCd: moneyCdList[0].minorCd,
			customType: customTypeList[0].minorCd,
			inserted: true,
		};
	}, [defaultMST2010_R01Dto, areaCdList, moneyCdList, customTypeList, validateCommonCodeLists]);

	const fetchOne = useCallback(async () => {
		if (!selectedMST2010_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getMST2010_R01One(selectedMST2010_R01.customCd);
			setEditMST2010_R01({ ...defaultMST2010_R01Row, ...response.data.data });
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [MST2010_R01Filter, setEditMST2010_R01, selectedMST2010_R01]);
	
	const initContainerData = useCallback(() => {	
		initData();
	}, [initData]);

	const handleCreateClick = useCallback(async () => {
		// 등록은 info(편집영역)만 초기화하고, 리스트/테이블은 유지
		initSelectedMST2010_R01();
		setEditMST2010_R01(defaultMST2010_R01Row());
	}, [initSelectedMST2010_R01, setEditMST2010_R01, defaultMST2010_R01Row])
	
	const handleDeleteClick = useCallback(async () => {
		if (!selectedMST2010_R01) {
			showWarning('선택된 거래처가 없습니다.');
			return;
		}
	
		if (!window.confirm('정말로 삭제하시겠습니까?')) {
			return;
		}
	
		await deleteMST2010_R01(selectedMST2010_R01.customCd);
		refetchUpdateList('delete', defaultMST2010_R01Row(), selectedMST2010_R01, 'customCd', MST2010_R01List, setMST2010_R01List);
		handleCreateClick();
		showSuccess(`${selectedMST2010_R01.customCd} 거래처 삭제가 완료되었습니다.`)
	}, [selectedMST2010_R01])

	const validateInsertData = (data: CustomerResponseDto) => {
		if (!validateString(data.customCd)) {
			showWarning('거래처 코드를 입력해주세요.'); return false;
		}
		if (!validateString(data.regNo)) {
			showWarning('사업자등록번호를 입력해주세요.'); return false;
		}
		if (!validateString(data.customNm)) {
			showWarning('상호명을 입력해주세요.\n거래처 코드 : ' + editMST2010_R01.customCd); return false;
		}
		if (!validateString(data.customNmFull)) {
			showWarning('상호명(Full)을 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.owner)) {
			showWarning('대표자명을 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.businessType)) {
			showWarning('업태를 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.businessClass)) {
			showWarning('종목을 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.telNo)) {
			showWarning('전화번호를 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.faxNo)) {
			showWarning('팩스번호를 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.taxTelNo)) {
			showWarning('세금계산서 연락처를 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		if (!validateString(data.taxEmail)) {
			showWarning('세금계산서 이메일을 입력해주세요.\n상호명 : ' + editMST2010_R01.customNm); return false;
		}
		
		return true; //  모든 검증 통과 시 true 반환
	}
	const handleSaveClick = useCallback(async () => {

		let valid = true;
		if(!validateInsertData(editMST2010_R01)) valid = false;
		if(!valid) { return; }

		let processName = '';
		let responseData: MST2010_R01Dto = { ...defaultMST2010_R01Row() };

		if (selectedMST2010_R01) {
			processName = 'patch';
			const response = await patchMST2010_R01(editMST2010_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editMST2010_R01.customCd} 거래처 수정이 완료되었습니다.`)
					setMessage(`${editMST2010_R01.customCd} 거래처 수정이 완료되었습니다.`)
				}else return;
			}
		} else {
			processName = 'post';
			const response = await createMST2010_R01(editMST2010_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editMST2010_R01.customCd} 거래처 등록이 완료되었습니다.`)
					setMessage(`${editMST2010_R01.customCd} 거래처 등록이 완료되었습니다.`)
				}else return;
			}
		}
	
		setTimeout(() => {
			setMessage("");
		}, 5000)

		if(editMST2010_R01) {
			refetchUpdateList(processName, responseData, editMST2010_R01, 'customCd', MST2010_R01List, setMST2010_R01List);
			setSelectedMST2010_R01(responseData);
		}
		// useCommonCodeStore의 거래처 리스트 업데이트
		await fetchCustomCdCode();
	
	}, [selectedMST2010_R01, editMST2010_R01, fetchCustomCdCode])

	useEffect(() => {
		if (selectedMST2010_R01) {
			fetchOne();
		}else{
			setEditMST2010_R01(defaultMST2010_R01Row());
		}
	}, [selectedMST2010_R01]);

	return {
		handleExcelClick,
		fetchData,
		defaultMST2010_R01Row,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	}
}
